import { analytics } from '~/services/analytics'
import { isIgnoredThirdPartyException } from '~/utils/posthogErrorFilter'
import { isNetworkFetchError } from '~/utils/networkError'

export const $istDevEnv = () => {
  // try catch falls nuxt nicht verfügbar
  try {
    const config = useRuntimeConfig()
    return config.public.NODE_ENV === 'development'
  }
  catch {
    return false
  }
  return false
}

export const $log = (...data: unknown[]) => {
  if ($istDevEnv() && data !== undefined && data !== null) {
    const t = new Date()
    console.log(`${t.getHours()}:${t.getMinutes() < 10 ? '0' + t.getMinutes() : t.getMinutes()}:${t.getSeconds()}:${t.getMilliseconds()} - `, ...data)
  }
}

export const $logError = (...data: unknown[]) => {
  if ($istDevEnv() && data !== undefined && data !== null) {
    const t = new Date()
    console.error(`${t.getHours()}:${t.getMinutes() < 10 ? '0' + t.getMinutes() : t.getMinutes()}:${t.getSeconds()}:${t.getMilliseconds()} - `, ...data)
  }
}

/** Narrow an `unknown` catch value to an Error; falls back to a synthesized Error carrying the original. */
export const toError = (value: unknown): Error => {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  try {
    return new Error(JSON.stringify(value))
  }
  catch {
    return new Error('Unknown error')
  }
}

/** Pull a readable message out of an unknown catch value; never throws. */
export const errorMessage = (value: unknown): string => toError(value).message

/** Pull a Firebase-style `code` string out of an unknown catch value, if present. */
export const getErrorCode = (value: unknown): string | undefined => {
  if (typeof value !== 'object' || value === null || !('code' in value)) {
    return undefined
  }
  const code = (value as { code: unknown }).code
  return typeof code === 'string' ? code : undefined
}

const INVALID_LOGIN_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/wrong-password',
  'auth/user-not-found',
])

const EXPECTED_NETWORK_ERROR_CODES = new Set([
  'auth/network-request-failed',
])

const FIREBASE_SESSION_EXPIRED_ERROR_CODES = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/argument-error',
  'auth/requires-recent-login',
])

/** Wrong email/password or unknown user — expected user input, not an app error. */
export const isInvalidLoginCredentialError = (value: unknown): boolean => {
  const code = getErrorCode(value)
  return code !== undefined && INVALID_LOGIN_CREDENTIAL_CODES.has(code)
}

/** Browser/Firebase network failures are expected runtime conditions, not app exceptions. */
export const isExpectedNetworkError = (value: unknown): boolean => {
  if (isNetworkFetchError(value)) return true

  const code = getErrorCode(value)
  return code !== undefined && EXPECTED_NETWORK_ERROR_CODES.has(code)
}

/** Firebase auth errors signalling the session is no longer valid (e.g. tab left open overnight). */
export const isFirebaseSessionExpiredError = (value: unknown): boolean => {
  const code = getErrorCode(value)
  return code !== undefined && FIREBASE_SESSION_EXPIRED_ERROR_CODES.has(code)
}

const isFetchResponse = (value: unknown): value is Response =>
  typeof value === 'object' && value !== null && 'status' in value && 'clone' in value

const getHttpResponse = (error: unknown): Response | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined
  }
  const response = (error as { response: unknown }).response
  return isFetchResponse(response) ? response : undefined
}

/** Proxy/gateway blips (often empty-body 502/503/504) — infra noise, not app bugs. */
const isGatewayHttpError = (error: unknown): boolean => {
  const status = getHttpResponse(error)?.status
  return status === 502 || status === 503 || status === 504
}

async function captureErrorForPostHog(message: string, error?: unknown) {
  if (
    isIgnoredThirdPartyException(error)
    || isIgnoredThirdPartyException(message)
    || isExpectedNetworkError(error)
    || isGatewayHttpError(error)
  ) {
    return
  }

  const response = error !== undefined ? getHttpResponse(error) : undefined
  const err = error instanceof Error
    ? error
    : error !== undefined
      ? toError(error)
      : new Error(message)

  const extra: Record<string, unknown> = { message }
  if (response) {
    extra.httpStatus = response.status
    try {
      const cloned = response.clone()
      const contentType = cloned.headers.get('content-type') ?? ''
      extra.responseBody = contentType.includes('json')
        ? await cloned.json()
        : await cloned.text()
    }
    catch { /* ignore unreadable bodies */ }
  }
  else if (error !== undefined && !(error instanceof Error)) {
    extra.detail = error
  }

  analytics.captureException(err, extra)
}

export const $reportError = (message: string, error?: unknown) => {
  $logError(message, error)
  if (import.meta.client && !$istDevEnv()) {
    void captureErrorForPostHog(message, error).catch(() => {})
  }
}

// Format duration in seconds to HH:MM:SS
export const formatDuration = (seconds: number): string => {
  if (!seconds) return '00:00:00'

  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  // Pad minutes and seconds with leading zeros
  const hDisplay = h < 10 ? `0${h}` : h
  const mDisplay = m < 10 ? `0${m}` : m
  const sDisplay = s < 10 ? `0${s}` : s

  return `${hDisplay}:${mDisplay}:${sDisplay}`
}
